import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getEquipment } from '../../../redux/selectors/equipment';
import { attachDefaultUnits } from '../../../redux/selectors/points';
import { groupListByProperty } from '../../../utils/collections';
import PointTrendContainer from '../../trends/PointTrendContainer';

/**
 * A component to display multiple point trend graphs for a single piece of equipment
 *
 * We make a new graph either for each measurement ('Sensors measuring Temperature') or,
 * for sensors that measure `Ordinal` values, for each invidual point.
 */
const EquipmentTrendsByUnit = ({ measurements, trendsByMeasurement, trendsByPointType }) => (
  <div style={{ width: '100%' }}>
    {measurements.map(({ id, name }) => (
      <PointTrendContainer
        key={id}
        title={`Sensors measuring ${name}`}
        pointIds={trendsByMeasurement[id]}
        variant="byUnit"
      />
    ))}
    {Object.entries(trendsByPointType).map(([pt, points]) => (
      <PointTrendContainer
        key={pt}
        title={`Sensors measuring ${pt}`}
        pointIds={points}
        variant="byType"
      />
    ))}
  </div>
);

EquipmentTrendsByUnit.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  equipmentId: PropTypes.number.isRequired, // used in redux connect
};

export default connect((state, { equipmentId }) => {
  const {
    entities: {
      pointTypes: { byId: ptById },
      measurements: { byId: measById },
    },
  } = state;
  const equipment = getEquipment(state, equipmentId);
  const pointsWithUnits = attachDefaultUnits(state, equipment.points);
  const equipmentByUnit = groupListByProperty('defaultUnit', pointsWithUnits);

  // show sensors with the same measurement in a graph together
  // except for 'Ordinal', which measures lots of different stuff and should be separated
  const trendsByMeasurement = pointsWithUnits
    .filter((p) => p.defaultUnit !== 'Ordinal')
    .reduce((map, p) => {
      const pointType = ptById[p.pointTypeId];
      const measurementId = pointType?.measurementId;
      if (measurementId != null) {
        const current = map[measurementId] || [];
        current.push(p.id);
        // eslint-disable-next-line no-param-reassign
        map[measurementId] = current;
      }
      return map;
    }, {});
  const measurements = Object.keys(trendsByMeasurement)
    .map((id) => measById[id])
    .filter((m) => m != null);

  // Here we handle the ordinal units, splitting them by point type (more finely than measurement)
  const trendsByPointType = (equipmentByUnit.Ordinal || []).reduce((obj, equip) => {
    const key = ptById[equip.pointTypeId].name;
    const entry = obj[key] || [];
    entry.push(equip.id);
    // eslint-disable-next-line no-param-reassign
    obj[key] = entry;
    return obj;
  }, {});

  return {
    measurements,
    trendsByMeasurement,
    trendsByPointType,
  };
})(EquipmentTrendsByUnit);
